import { Suspense } from "react"
import { makeStyles } from "tss-react/mui"
import { useLocation, useNavigate } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Grid, Skeleton, Stack, Typography } from "@mui/material"
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons"
import { QueryErrorBoundary } from "../../../../core/api/components/QueryErrorBoundary"
import { CardGroup } from "../../../merchant/merchant/home/components/CardGroup"
import { MERCHANT_ADMIN_ROUTE_DEVICES } from "../../../../constants/routes"
import { fetchDeviceReport, SupplierHomeQuery } from "../supplierHomeApi"
import {
    DeviceReportAlertAccordion,
    getSeverityIcon,
} from "../../../../shared/components/alert/DeviceReportAlertAccordion"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { pushContext } from "../../../context/contextSlice"
import { useAppDispatch } from "../../../store/storeHooks"
import { MerchantTypeReport } from "../supplierHomeTypes"
import { useSupplier } from "../../supplierHooks"

const useStyles = makeStyles()(() => ({
    button: {
        cursor: "pointer",
        transition: "all ease-out 0.3s",
        "&:hover": {
            transform: "scale(1.01)",
            paddingLeft: "0.5rem",
        },
    },
}))

export function DeviceReportContent() {
    const { supplierId } = useSupplier()
    const { pathname } = useLocation()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { classes } = useStyles()

    const { data: report } = useApiQuery(fetchDeviceReport, {
        queryName: SupplierHomeQuery.DEVICE_REPORT,
        dependencies: {
            supplierId,
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: 30000,
    })

    const handleClick = (merchant: MerchantTypeReport) => {
        dispatch(
            pushContext({
                type: "merchant",
                merchant: {
                    ...merchant,
                    caseNumber: "",
                    addressDisplayText: "",
                },
                returnUrl: pathname,
            }),
        )

        navigate(MERCHANT_ADMIN_ROUTE_DEVICES)
    }

    const renderDeviceList = (merchants: MerchantTypeReport[]) => (
        <Stack gap={3}>
            {merchants.map((merchant) => (
                <Stack key={merchant.id} alignItems="flex-start" gap={2}>
                    <Stack
                        flexDirection="row"
                        alignItems="center"
                        gap={1}
                        onClick={() => handleClick(merchant)}
                        className={classes.button}
                    >
                        <Typography fontWeight={700}>{merchant.name}</Typography>
                        <FontAwesomeIcon icon={faChevronRight} />
                    </Stack>

                    <Grid container direction="column" gap={2}>
                        {merchant.devices.map((device) => (
                            <Stack flexDirection="row" alignItems="center" gap={1} key={device.id}>
                                {getSeverityIcon(device.status.state)}
                                <Typography>{device.status?.message ?? ""}</Typography>
                            </Stack>
                        ))}
                    </Grid>
                </Stack>
            ))}
        </Stack>
    )

    if (!report || !report?.deviceReport) return null

    return (
        <CardGroup>
            <DeviceReportAlertAccordion
                status={report.deviceReport.state}
                title={report.deviceReport.title}
                message={report.deviceReport.message}
            >
                {renderDeviceList(report.merchants)}
            </DeviceReportAlertAccordion>
        </CardGroup>
    )
}

export function DeviceReport() {
    return (
        <QueryErrorBoundary>
            <Suspense
                fallback={
                    <>
                        <Skeleton variant="rectangular" animation="wave" width="100%" height="4.5rem" />
                    </>
                }
            >
                <DeviceReportContent />
            </Suspense>
        </QueryErrorBoundary>
    )
}
